import axios from "axios";
import api from "../api";
import authService from "./AuthService";

const END_POINT = "/api/interventions";
const MASTER = "/masters";
const NO_MASTER = "/sub";

/**
 * This method will load all interventions (without master intervention) from the database with a get http call
 * @returns {} it is returning a promise with all interventions (without master intervention)
 */
export async function getAllInterventionsHttp() {
  return await api.get(`${END_POINT}`);
}

/**
 * This method will load only master intervention from the database with a get http call
 * @returns {} it is returning a promise with only master intervention
 */
export async function getMasterInterventionsHttp() {
  return await api.get(`${END_POINT}${MASTER}`);
}

/**
 * This method will load the selected master intervention with a get http call
 * @param  {long} id id of the selected master intervention
 * @returns {} it is returning a promise with the master intervention selected
 */
export async function getMasterInterventionById(id) {
  return await api.get(`${END_POINT}${MASTER}/${id}`);
}

/**
 * This method will load all interventions (without master intervention), depending of userType, dateStart and dateEnd parameters, from the database with a get http call
 *
 * @param  {String} userType
 * @param  {String} dateStart
 * @param  {String} dateEnd
 * @returns {} it is returning a promise with all interventions (without master intervention) depending of parameters
 */
export async function getSubInterventionsHttp(userType, dateStart, dateEnd) {
  return await api.get(`${END_POINT}${NO_MASTER}`, {
    params: {
      type: userType,
      start: dateStart,
      end: dateEnd,
    },
  });
}

/**
 * This method will load all Advanced interventions (without master intervention), depending of userType, dateStart and dateEnd parameters, from the database with a get http call
 *
 * @param  {String} userType
 * @param  {String} dateStart
 * @param  {String} dateEnd
 * @returns {} it is returning a promise with all Advanced interventions (without master intervention) depending of parameters
 */
export async function getAdvSubInterventionsHttp(userType, dateStart, dateEnd) {
  return await api.get(`${END_POINT}/adv-sub`, {
    params: {
      type: userType,
      start: dateStart,
      end: dateEnd,
    },
  });
}

/**
 * This method will load all children interventions from the selected master intervention
 *
 * @param  {long} masterId id from the master intervention selected
 * @returns {} it is returning a promise with all children interventions from the selected master intervention
 */
export async function getSubInterventionsByMasterId(masterId) {
  return await api.get(`${END_POINT}${MASTER}/${masterId}${NO_MASTER}`);
}

/**
 * This method is adding an intervention from the database with a post http call
 *
 * @param  {Object - interventionDto} intervention object that we want to add from the database
 * @returns {} it is returning a promise with the added intervention
 */
export async function addInterventionHttp(intervention) {
  return await api.post(`${END_POINT}`, {
    comment: intervention.comment,
    locationId: intervention.locationId,
    courseId: intervention.courseId,
    userId: intervention.userId,
    attendeesCount: intervention.attendeesCount,
    type: intervention.type,
    validated: intervention.validated,
    dateStart: intervention.dateStart,
    dateEnd: intervention.dateEnd,
    timeStart: intervention.timeStart,
    timeEnd: intervention.timeEnd,
    masterInterventionId: intervention.masterInterventionId,
    master: intervention.master,
    version: intervention.version,
  });
}

/**
 * This method is updating an intervention from the database with a put http call
 *
 * @param  {Object - interventionDto} intervention object that we want to update from the database
 * @returns {} it is returning a promise with the updated intervention
 */
export async function updateInterventionHttp(intervention) {
  return await api.put(`${END_POINT}`, {
    id: intervention.id,
    idDg2: intervention.idDg2,
    slug: intervention.slug,
    comment: intervention.comment,
    locationId: intervention.locationId,
    locationIdDg2: intervention.locationIdDg2,
    courseId: intervention.courseId,
    courseIdDg2: intervention.courseIdDg2,
    userId: intervention.userId,
    attendeesCount: intervention.attendeesCount,
    type: intervention.type,
    validated: intervention.validated,
    dateStart: intervention.dateStart,
    dateEnd: intervention.dateEnd,
    timeStart: intervention.timeStart,
    timeEnd: intervention.timeEnd,
    masterInterventionId: intervention.masterInterventionId,
    master: intervention.master,
    version: intervention.version,
  });
}

/**
 * This method is removing a intervention from the database with a delete http call
 *
 * @param  {long} id id of the intervention we want to remove from the database
 * @returns {} it is returning a promise. You will have 202 status if the intervention was deleted successfully.
 */
export async function deleteInterventionHttp(id) {
  return await api.delete(`${END_POINT}/${id}`);
}

/**
 * This method will give the number of interventions containing 'search' with a get http call
 * @param  {String} userType only interventions containing 'search' will be counted
 * @returns {} it is returning a promise with the number of interventions containing 'search'
 */
export async function countInterventionsByUserType(userType) {
  return await api.get(`${END_POINT}/count`, {
    params: { type: userType },
  });
}

/**
 * This method will fetch users from the webservice Wdg2 with a get http call
 *  @param  {Object - LogInUser} logInUser user dg2
 */
export async function fetchAllEventsDG2Http(logInUser, year) {
  return await axios.get(
    `${process.env.VUE_APP_BASE_URL}${END_POINT}/dg2/${year.dateStart}/${year.dateEnd}`,
    {
      headers: {
        Authorization: authService.getBearerToken(),
        "X-AUTH-TOKEN": `${logInUser.email}:${logInUser.password}`,
      },
      timeout: 500000,
    }
  );
}

/**
 * Copy an intervention and link them to a master intervention
 *
 * @param  {number} id of the intervention to split
 * @param  {[object]} dateRange Object containing id, dates and times for the event
 * @returns {} it is returning a promise
 */
export async function splitIntervention(id, dateRanges) {
  return await api.post(
    `${process.env.VUE_APP_BASE_URL}${END_POINT}/split/${id}`,
    dateRanges
  );
}

/**
 * Export and upload calendar
 *
 * @param  {number} id of the user
 * @param  {string} filename name of the file to be downloaded
 * @returns {} it is returning a promise or 404 if id not found
 */
export async function exportCalendar(id, filename) {
  return await api
    .get(`${process.env.VUE_APP_BASE_URL}${END_POINT}/ical/${id}`, {
      responseType: "blob",
    })
    .then((res) => {
      const blob = new Blob([res.data], { type: "application/ics" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    })
    .catch((error) => {
      return error.response.status;
    });
}

/**
 * This method will load all interventions (without master intervention) for given User and starting after given date,
 * from the database  with a get http call
 * @param {number} userId The Id of the user which Interventions we want
 * @param {string} date the minimum starting date of the Intervention
 * @returns {} it is returning a promise with all interventions for given UserId after date
 */
export async function getAllInterventionsByUserIdAfterDateHttp(userId, date) {
  return await api.get(`${END_POINT}/user/${userId}/${date}`);
}

/**
 * This return quotations in DG2 for the selected intervention
 * @param {Number} courseId id of the course
 * @param {Number} locationId id of the location
 * @returns a list of quotations
 */
export async function getInterventionQuotationsHttp(courseId, locationId) {
  return await api.get(
    `${END_POINT}/import-quotations/${courseId}/${locationId}`
  );
}
